import _ from 'lodash'
import type { BaseDataItem, RefSchemas } from '@wix/thunderbolt-becky-types'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import type { DataNode } from './types'

type ItemRefs = {
	alreadyResolved: boolean
	refs: Array<{ innerPath: ReadonlyArray<string>; referencedMap: string }>
}

export function findDataItemRefs(
	dataItem: BaseDataItem,
	dataItemPath: Array<string>,
	dynamicSchemas: RefSchemas,
	useDynamicRefSchemas: boolean,
): ItemRefs {
	let alreadyResolved = false
	const [dataMap] = dataItemPath
	const hasDynamicSchema = dataMap in dynamicSchemas
	const hasStaticSchema = `${dataMap}.json` in schemas
	if ((useDynamicRefSchemas && !hasDynamicSchema && !hasStaticSchema) || (!useDynamicRefSchemas && !hasStaticSchema)) {
		// console.warn(`${dataItemPath}: Missing schema for data map "${dataMap}".`)
		return {
			alreadyResolved: false,
			refs: [],
		}
	}

	if (!useDynamicRefSchemas) {
		if (dataMap === 'innerElements' && dataItem.type !== 'InnerElement') {
			return {
				alreadyResolved: false,
				refs: _.keys(dataItem)
					.filter((key) => !['id', 'type', 'metaData'].includes(key))
					.map((key) => ({
						innerPath: [key],
						referencedMap: 'innerElements',
					})),
			} as ItemRefs
		}
	}

	const getSchema = (mapName: string, type: string) => {
		const staticSchema = (schemas as RefSchemas)[`${mapName}.json`]?.[type]

		if (!useDynamicRefSchemas) {
			return staticSchema
		}

		const dynamicSchema = dynamicSchemas[mapName]?.[type]
		return dynamicSchema || staticSchema
	}

	const refs = getSchema(dataMap, dataItem.type)
	if (!refs) {
		// console.warn(`${dataItemPath}: Missing schema for type "${dataMap}/${dataItem.type}".`)
		return {
			alreadyResolved: false,
			refs: [],
		}
	}
	const fixedRefs = refs
		.filter((ref) => {
			const pointer = _.get(dataItem, ref.path)
			if (!pointer) {
				// console.log('Missing ref for ', ref.path, 'in', dataItemId)
				return false
			}
			if (typeof pointer === 'object' && !Array.isArray(pointer)) {
				alreadyResolved = true
				// console.log('Ref is not a string', ref.path, 'in', dataItemId)
				return false
			}
			if (ref.isList && pointer.some((nestedPointer: any) => typeof nestedPointer !== 'string')) {
				alreadyResolved = true
				// console.log('ListRef contain non-string items:', ref.path, 'in', dataItemId)
				return false
			}
			return true
		})
		.map((ref) => ({
			innerPath: ref.path,
			referencedMap: ref.referencedMap,
		}))
	return {
		alreadyResolved,
		refs: fixedRefs,
	}
}

export const defaultDataNode: DataNode<any> = {
	execute(dataItem, resolvedRefs) {
		return _.merge({}, dataItem, resolvedRefs)
	},
}
